import { CellValueChangedEvent, ColDef, GridApi, ICellRendererParams, RowDragEndEvent } from 'ag-grid-community'
import { AgGridReact } from 'ag-grid-react'
import { LoadingBox } from '@outbound-ai/ursa'
import { isEmpty } from 'lodash'
import { memo, useEffect, useMemo, useState } from 'react'
import { Form } from 'react-bootstrap'
import { FaEdit, FaPlus, FaTrash } from 'react-icons/fa'
import { Button } from '@outbound-ai/ursa'
import styled from 'styled-components'
import { ColorsEnum, ColorsToneEnum } from 'types/cssTypes'
import { ErrorMessagesEnum } from 'types/errorTypes'
import { notificator } from 'utils/notificator/notificator'
import { CsvPreview } from '../../calls/components/CsvPreview/CsvPreview'
import { useDdfColumSettingsAvailable } from '../../claims/claimsHooks'
import { settingsServiceApiPaths, setUserPreference } from '../settingsApi'
import { useTenantPreference } from '../settingsHooks'
import {
  ColumnSettingTypeEnum,
  IColumnSetting,
  IDDFColumnSetting,
  IFileCenterColumnSettings,
  IFileCenterSampleData,
  UserPreferencesKeysEnum,
} from '../settingsTypes'
import { parseJSON } from '../settingsUtils'
import { SettingsAddEditColumnSettingModal } from './SettingsAddEditColumnSettingModal'

enum ColumnActionEnum {
  add,
  edit,
  reorder,
}

const columnActionNotificationMessage = (action: ColumnActionEnum) => {
  switch (action) {
    case ColumnActionEnum.add:
      return `Column rule added`
    case ColumnActionEnum.edit:
      return `Column rule edited`
    case ColumnActionEnum.reorder:
      return `Rules reordered`
  }
}

const renumber = (columnSettings: IColumnSetting[]) => {
  columnSettings.forEach((columnSetting, index) => {
    columnSetting.sequence = index
  })
}

export const SettingsColumns = () => {
  const {
    data: columnSettingsExperimentJson,
    error: columnSettingsReadError,
    isLoading,
    mutate,
  } = useTenantPreference(UserPreferencesKeysEnum.fileCenterColumnSettingsPractice)

  const { data: fileCenterSampleDataJson, error: fileCenterSampleDataReadError } = useTenantPreference(
    UserPreferencesKeysEnum.fileCenterSettingsSampleData,
  )
  const [fileCenterSampleData, setFileCenterSampleData] = useState<IFileCenterSampleData>()

  useEffect(() => {
    if (fileCenterSampleDataJson?.value) {
      const parsedSampleData = parseJSON(fileCenterSampleDataJson.value)
      if (!isEmpty(parsedSampleData) && typeof parsedSampleData === 'object') {
        const fileCenterSampleDataNew = parsedSampleData as IFileCenterSampleData
        setFileCenterSampleData(fileCenterSampleDataNew)
      }
    }
  }, [fileCenterSampleDataJson, fileCenterSampleDataReadError])

  const [gridApi, setGridApi] = useState<GridApi | null>(null)
  const [fileCenterColumnSettings, setFileCenterColumnSettings] = useState<IFileCenterColumnSettings>({
    version: 1,
    columnSettings: [],
  })
  const [isUpdateSettingsLoading, setIsUpdateSettingsLoading] = useState(false)
  const { data: iddfColumnSettingsAvailable } = useDdfColumSettingsAvailable()
  const [iddfColumnSettingMap, setIddfColumnSettingsMap] = useState<Map<
    ColumnSettingTypeEnum,
    IDDFColumnSetting
  > | null>(null)

  useEffect(() => {
    if (columnSettingsExperimentJson?.value) {
      const parsedColumnSettingsPractice = parseJSON(columnSettingsExperimentJson.value)
      if (!isEmpty(parsedColumnSettingsPractice) && typeof parsedColumnSettingsPractice === 'object') {
        const columnSettings = (parsedColumnSettingsPractice as IFileCenterColumnSettings).columnSettings?.map(
          (columnSetting) => {
            if (!columnSetting.type) console.error('column setting has no type.')
            return columnSetting
          },
        )
        setFileCenterColumnSettings({
          ...(parsedColumnSettingsPractice as IFileCenterColumnSettings),
          columnSettings: columnSettings,
        })
      }
    }
  }, [columnSettingsExperimentJson, columnSettingsReadError])

  useEffect(() => {
    columnSettingsReadError &&
      notificator.notify({
        heading: columnSettingsReadError.message || ErrorMessagesEnum.ResourceNotFound,
        type: 'error',
      }) //notificator.error(columnSettingsReadError.message || ErrorMessagesEnum.ResourceNotFound)
  }, [columnSettingsReadError])

  useEffect(() => {
    renumber(fileCenterColumnSettings.columnSettings)
  }, [fileCenterColumnSettings])

  useEffect(() => {
    if (iddfColumnSettingsAvailable) {
      const iddfColumnSettingMapInProgress = new Map<ColumnSettingTypeEnum, IDDFColumnSetting>()
      iddfColumnSettingsAvailable.forEach((iddfColumnSetting) =>
        iddfColumnSettingMapInProgress.set(iddfColumnSetting.type, iddfColumnSetting),
      )
      setIddfColumnSettingsMap(iddfColumnSettingMapInProgress)
      const ColumnsInRenderer = memo(function ColumnsInRenderer(params: ICellRendererParams<IColumnSetting>) {
        if (!params.data) return <>(no input columns)</>
        if (!iddfColumnSettingMapInProgress) return <>(loading)</>
        const columnSetting = params.data
        const iddfSetting = iddfColumnSettingMapInProgress.get(columnSetting.type as ColumnSettingTypeEnum)
        const columnsIn =
          columnSetting.typeSpecificConfiguration && iddfSetting
            ? iddfSetting.fieldsForKeysIn.map((key) => columnSetting.typeSpecificConfiguration[key]).join(', ')
            : '-'
        return <span>{columnsIn}</span>
      })
      const ColumnsOutRenderer = memo(function ColumnsOutRenderer(params: ICellRendererParams<IColumnSetting>) {
        if (!params.data) return <>(no Output columns)</>
        if (!iddfColumnSettingMapInProgress) return <>(loading)</>
        const columnSetting = params.data
        const iddfSetting = iddfColumnSettingMapInProgress.get(columnSetting.type as ColumnSettingTypeEnum)
        const columnsOut =
          columnSetting.typeSpecificConfiguration && iddfSetting
            ? iddfSetting.fieldsForKeysOut.map((key) => columnSetting.typeSpecificConfiguration[key]).join(', ')
            : '-'
        return <span>{columnsOut}</span>
      })

      const EditButtonRenderer = memo(function EditButtonRenderer(params: ICellRendererParams<IColumnSetting>) {
        if (!params.data) return <>(no data)</>
        const iddfSetting = iddfColumnSettingMapInProgress?.get(params.data.type as ColumnSettingTypeEnum)
        return (
          <Action className="action-holder">
            <Form>
              <>
                {iddfSetting ? (
                  <SettingsAddEditColumnSettingModal
                    iddfType={iddfSetting}
                    title={
                      <>
                        <FaEdit />
                      </>
                    }
                    handleAddColumnSetting={handleEditColumnSetting}
                    columnSetting={params.data}
                  />
                ) : (
                  <span>x</span>
                )}
              </>
            </Form>
            <span>
              <Button onClick={() => handleDeleteColumnSetting(params.data as IColumnSetting)} variant="danger">
                <FaTrash className="delete-button" />
              </Button>
            </span>
          </Action>
        )
      })

      setColumnDefs([
        {
          field: 'description',
          filter: true,
          editable: true,
          minWidth: 100,
          pinned: 'left',
          lockPinned: true,
          lockPosition: 'left',
          rowDrag: true,
        },
        {
          field: 'type',
          width: 200,
          filter: true,
          editable: true,
          pinned: false,
        },
        {
          field: 'in',
          filter: true,
          editable: false,
          pinned: false,
          cellRenderer: ColumnsInRenderer,
        },
        {
          field: 'out',
          filter: true,
          editable: false,
          pinned: false,
          cellRenderer: ColumnsOutRenderer,
        },
        /*
        {
          field: 'uuid',
          filter: true,
          editable: false,
          minWidth: 350,
          pinned: false,
        },
         */
        {
          field: 'edit',
          minWidth: 75,
          filter: false,
          editable: false,
          lockPinned: true,
          lockPosition: 'right',
          cellRenderer: EditButtonRenderer,
        },
      ])
    }
  }, [iddfColumnSettingsAvailable])

  const handleAddColumnSetting = async (newColumnSetting: IColumnSetting) => {
    setFileCenterColumnSettings((fcColumnSettings) => {
      const newFcColumnSettings = {
        ...fcColumnSettings,
        columnSettings: [...fcColumnSettings.columnSettings, newColumnSetting],
      }
      onUpdateSettings(newFcColumnSettings, ColumnActionEnum.add)

      return newFcColumnSettings
    })
  }

  const handleDeleteColumnSetting = async (columnSettingToDelete: IColumnSetting) => {
    setFileCenterColumnSettings((fcColumnSettings) => {
      const newFcColumnSettings = {
        ...fcColumnSettings,
        columnSettings: fcColumnSettings.columnSettings.filter(
          (columnSettingInspect) => columnSettingInspect.uuid !== columnSettingToDelete.uuid,
        ),
      }
      renumber(newFcColumnSettings.columnSettings)
      onUpdateSettings(newFcColumnSettings, ColumnActionEnum.edit)
      return newFcColumnSettings
    })
  }

  const handleEditColumnSetting = async (columnSettingEdited: IColumnSetting) => {
    setFileCenterColumnSettings((fcColumnSettings) => {
      const columnSettingsEdited = fcColumnSettings.columnSettings.map((columnSettingExisting) =>
        columnSettingExisting.uuid == columnSettingEdited.uuid ? columnSettingEdited : columnSettingExisting,
      )
      renumber(columnSettingsEdited)
      const newSettings = {
        ...fcColumnSettings,
        columnSettings: columnSettingsEdited,
      }
      onUpdateSettings(newSettings, ColumnActionEnum.edit)
      return newSettings
    })
  }

  const moveColumnSetting = async (indexOld: number, indexNew: number) => {
    setFileCenterColumnSettings((fcColumnSettings) => {
      if (
        0 > indexOld ||
        fcColumnSettings.columnSettings.length <= indexOld ||
        0 > indexNew ||
        fcColumnSettings.columnSettings.length <= indexNew
      ) {
        console.error(`error 28273efa-0137-491b-8be8-0f6087f02752: indices out of range, order settings not reordered.`)
        return fcColumnSettings
      }
      const columnsReorder = [...fcColumnSettings.columnSettings]
      columnsReorder.splice(indexNew, 0, columnsReorder.splice(indexOld, 1)[0])
      renumber(columnsReorder)
      const newSettings = {
        ...fcColumnSettings,
        columnSettings: columnsReorder,
      }
      onUpdateSettings(newSettings, ColumnActionEnum.reorder)
      return newSettings
    })
  }

  const onUpdateSettings = async (fileCenterColumnSettings: IFileCenterColumnSettings, action: ColumnActionEnum) => {
    try {
      setIsUpdateSettingsLoading(true)
      await setUserPreference(settingsServiceApiPaths.tenantPreferencesPut(), {
        key: UserPreferencesKeysEnum.fileCenterColumnSettingsPractice,
        value: JSON.stringify(fileCenterColumnSettings),
      })
      await mutate()
      notificator.notify({ heading: columnActionNotificationMessage(action), type: 'success' })
      //notificator.success(columnActionNotificationMessage(action))
    } catch (e: any) {
      notificator.notify({ heading: e.message || ErrorMessagesEnum.SomethingWentWrong, type: 'error' })
      //notificator.error(e.message || ErrorMessagesEnum.SomethingWentWrong)
    } finally {
      setIsUpdateSettingsLoading(false)
    }
  }

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: false,
      resizable: true,
      width: 150,
    }),
    [],
  )
  const [columnDefs, setColumnDefs] = useState<ColDef[]>()

  async function onCellValueChanged(event: CellValueChangedEvent<IColumnSetting>) {
    await handleEditColumnSetting(event.data)
  }

  async function onRowDragEnd(event: RowDragEndEvent) {
    await moveColumnSetting(event.node.data.sequence, event.overIndex)
  }

  const debugHelp = (
    <ul>
      <>
        <p>
          Version is {fileCenterColumnSettings.version}. You have {fileCenterColumnSettings.columnSettings.length}{' '}
          settings:
        </p>
        {fileCenterColumnSettings.columnSettings.map((column, index) => {
          return (
            <li key={index}>
              i:{index} seq:{column.sequence} desc:&apos;{column.description}&apos;{' '}
              {column.typeSpecificConfiguration?.toString()}
            </li>
          )
        })}
      </>
    </ul>
  )
  return (
    <Div>
      {isLoading && <LoadingBox />}

      {!isLoading && (
        <>
          <div className="section-header">
            <h2>Column Settings</h2>
            <p>Create and edit the rules to use when uploading a CSV file in File Center.</p>
            {false && debugHelp}
          </div>
          <div className="section-rules">
            <div className="column-rules">
              <details className="rules-add">
                <summary>Select a type of rule to add</summary>
                <Form>
                  <>
                    {isUpdateSettingsLoading && <LoadingBox />}
                    {!iddfColumnSettingMap ? (
                      <>loading</>
                    ) : (
                      Array.from(iddfColumnSettingMap.values()).map((iddf) => {
                        return (
                          <section className="mt-1" key={iddf.type}>
                            <SettingsAddEditColumnSettingModal
                              iddfType={iddf}
                              title={
                                <>
                                  <FaPlus /> Add {iddf.description}
                                </>
                              }
                              handleAddColumnSetting={handleAddColumnSetting}
                            />
                          </section>
                        )
                      })
                    )}
                  </>
                </Form>
              </details>
            </div>
            <div className="rules-grid">
              {!iddfColumnSettingMap ? (
                <>loading</>
              ) : (
                <AgGridReact<IColumnSetting>
                  className="ag-theme-alpine ag-theme-outbound"
                  rowData={fileCenterColumnSettings.columnSettings}
                  columnDefs={columnDefs}
                  defaultColDef={defaultColDef}
                  animateRows={true}
                  suppressRowClickSelection={true}
                  suppressCellFocus={true}
                  //* Pagination and dragging
                  //pagination={true}
                  //paginationAutoPageSize={true}
                  //* Tooltip
                  tooltipShowDelay={0}
                  tooltipHideDelay={2000}
                  loadingOverlayComponent={LoadingBox}
                  onGridReady={(params) => {
                    if (!fileCenterColumnSettings.columnSettings.length) {
                      params.api.showLoadingOverlay()
                    }
                    !gridApi && setGridApi(params.api)
                  }}
                  //* Alows to select text from the grid
                  //ensureDomOrder={true}
                  //enableCellTextSelection={true}
                  rowDragManaged={true}
                  suppressDragLeaveHidesColumns={true}
                  onCellValueChanged={onCellValueChanged}
                  onRowDragEnd={onRowDragEnd}
                />
              )}
            </div>
          </div>
          <div className="preview-container">
            {!fileCenterSampleData ? (
              <div>Loading...</div>
            ) : (
              <CsvPreview
                columnSettings={fileCenterColumnSettings.columnSettings}
                fileCenterSampleData={fileCenterSampleData}
                justSniff={false}
              />
            )}
          </div>
        </>
      )}
    </Div>
  )
}

const Div = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border-radius: 0.5rem;

  .section-header {
    height: fit-content;
  }
  .section-rules {
    height: 40%;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
  .preview-container {
    height: 50%;
  }
  .column-rules {
    height: fit-content;
  }
  .rules-add {
    height: fit-content;
  }
  .rules-grid {
    flex-grow: 1;
  }
  .column-preview {
    height: 100%;
  }

  > .header {
    input {
      padding-block: 0.75rem;
      width: 30ch;
    }
  }

  // These should be moved/changed when we have a final product decision on our tables design
  .ag-theme-outbound {
    --ag-alpine-active-color: ${({ theme }) => theme.colors.info.darker};
    --ag-foreground-color: ${({ theme }) => theme.colors.primary.darker};
    --ag-data-color: ${({ theme }) => theme.colors.grey.darker};
    --ag-data-color: ${({ theme }) => theme.colors.grey.darker};
    --ag-header-foreground-color: ${({ theme }) => theme.colors.primary.darker};
    --ag-header-background-color: ${({ theme }) => theme.colors.tertiary.lighter};
    --ag-selected-row-background-color: ${({ theme }) => theme.colors.info.light};
    --ag-row-hover-color: ${({ theme }) => theme.colors.info.lightest};
    --ag-border-color: transparent;
    /* --ag-borders-critical: solid 1px; */
    --ag-checkbox-border-radius: ${({ theme }) => theme.borderRadius.circle};
    /* --ag-icon-size: 2rem; */
    --ag-font-family: ${({ theme }) => theme.fontFamily.milliard};
  }

  .column-settings {
    .section-header {
      color: ${({ theme }) => theme.colors.grey.darker};
      h2 {
        font-size: ${({ theme }) => theme.util.pixelToRem(18)};
      }
      p {
        font-size: ${({ theme }) => theme.util.pixelToRem(13)};
      }
    }

    form {
      > .form-check {
        padding: 0.675rem;
        padding-left: 2rem;
        display: flex;
        align-items: center;
        gap: 0.675rem;
        border-bottom: ${({ theme }) => theme.borders.s.solid(ColorsEnum.grey, ColorsToneEnum.light)};

        .form-check-input {
          margin-top: -0.25rem;
        }

        .form-check-input[disabled],
        .form-check-input[disabled] + label {
          opacity: 0.3;
        }

        label {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          cursor: pointer;
          ${({ theme }) => theme.typography.s.normal};

          .default-icon {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 2rem;
            height: 2rem;

            svg path {
              fill: ${({ theme }) => theme.colors.primary.light};
            }
          }
          .robot-tooltip .tooltip-container {
            left: 3rem;
            top: 0;
            max-width: 12rem;
            width: 12rem;
            background-color: ${({ theme }) => theme.colors.grey.lightest};
            color: ${({ theme }) => theme.colors.grey.darker};
            .tooltip-arrow::after {
              border-color: transparent ${({ theme }) => theme.colors.grey.lightest} transparent transparent;
            }
          }
          .default-phone-name {
            color: ${({ theme }) => theme.colors.grey.darker};
          }
        }
      }
    }

    button {
      display: flex;
      align-items: center;
      font-weight: ${({ theme }) => theme.fontWeight.normal};

      svg {
        position: relative;
        top: -0.125rem;
      }
    }
  }
`

const Action = styled.div`
  white-space: nowrap;
  display: flex;
  gap: 0.5rem;
  align-items: center;

  .action-button {
    color: ${({ theme }) => theme.colors.primary.neutral};
  }
  .delete-button {
    color: ${({ theme }) => theme.colors.warning.neutral};
  }
`
